import { createSharedState } from 'preact-shared-state-hook'
import { doodleStorageSize } from '../../../server/src/doodleStorageSize'
import type { Doodle, QueryWhere } from '../../../server/src/models'
import { URL_API_DOODLE, URL_API_DOODLES, URL_API_DOODLES_IN_FOLDER } from '../../../server/src/urls'
import { i18n } from '../../shared/i18n'
import { notifyError } from '../../shared/notifyError'
import { patchJson, withDefaultErrorHandler } from '../../shared/utils'
import { updateCurrentUser } from './account'
import { getFolder } from './folders'

const loadedFolderIds = new Set<string>()
const [useDoodleIdDoodleMap, setDoodleIdDoodleMap] = createSharedState<ReadonlyMap<string, Doodle>>(new Map())
const [useSearchResultMap, setSearchResultMap] = createSharedState<
  ReadonlyMap<string, Readonly<{ where: QueryWhere; doodleIds: readonly string[]; hitsTooMany?: boolean }>>
>(new Map())

export { useDoodleIdDoodleMap, useSearchResultMap }

const updateMap =
  <K, V>(update: (map: Map<K, V>) => void) =>
  (oldMap: ReadonlyMap<K, V>) => {
    const newMap = new Map(oldMap)
    update(newMap)
    return newMap
  }

export const findDoodleInMemory = (doodleId: string) => useDoodleIdDoodleMap.snapshot()?.get(doodleId)

export const loadFolderDoodles = async (folderId: string, force?: boolean) => {
  if (!force && loadedFolderIds.has(folderId)) {
    return
  }
  const response = await fetch(URL_API_DOODLES_IN_FOLDER(folderId))
  if (!response.ok) {
    return
  }
  const doodles = (await response.json()) as Doodle[]
  if (Array.isArray(doodles)) {
    setDoodleIdDoodleMap(updateMap(map => doodles.forEach(doodle => map.set(doodle.id, doodle))))
    loadedFolderIds.add(folderId)
  }
}

export const deleteDoodle = async (_doodle: Doodle) => {
  const doodle = useDoodleIdDoodleMap.snapshot().get(_doodle.id)
  if (!doodle) {
    throw Error()
  }
  if (await withDefaultErrorHandler(fetch(URL_API_DOODLE(doodle.id), { method: 'DELETE' }), notifyError)) {
    setDoodleIdDoodleMap(updateMap(map => map.delete(doodle.id)))
    const storageSize = doodleStorageSize(doodle)
    updateCurrentUser(currentUser => {
      if (getFolder(doodle.parentFolderId)?.tenantSharing) {
        return { ...currentUser, tenantStorageUsage: Math.max(0, (currentUser.tenantStorageUsage ?? 0) - storageSize) }
      } else {
        return { ...currentUser, storageUsage: Math.max(0, currentUser.storageUsage - storageSize) }
      }
    })
  } else {
    setDoodleIdDoodleMap(updateMap(map => map.set(doodle.id, doodle)))
  }
}

export const setDoodleParentFolder = async (doodleId: string, parentFolderId: string) => {
  const doodle = findDoodleInMemory(doodleId)
  if (!doodle) {
    throw Error()
  }
  const oldFolder = getFolder(doodle.parentFolderId)
  const newFolder = getFolder(parentFolderId)
  if (newFolder?.shared && doodle.uids.length !== 1) {
    return alert(i18n.sharing.sharedDoodleCannotMoveToSharedFolder)
  }
  const oldFolderTenantSharing = oldFolder?.tenantSharing
  const newFolderTenantSharing = newFolder?.tenantSharing
  if (oldFolderTenantSharing && !newFolderTenantSharing && !confirm(i18n.confirmMovingFromTenantSharedToPersonalFolder)) {
    return
  }
  if (oldFolder?.shared && !confirm(i18n.sharing.confirmUnsharingByMovingFromSharedFolder)) {
    return
  }
  const response = await withDefaultErrorHandler(patchJson(URL_API_DOODLE(doodleId), { parentFolderId }), notifyError)
  if (!response) {
    return
  }
  const updatedDoodle = (await response.json()) as Doodle
  setDoodleIdDoodleMap(updateMap(map => map.set(doodle.id, updatedDoodle)))
  if (!oldFolderTenantSharing === !newFolderTenantSharing) {
    return
  }
  updateCurrentUser(currentUser => {
    const storageSize = doodleStorageSize(doodle)
    const incrementalTenantStorageUsage = newFolderTenantSharing ? storageSize : -storageSize
    return {
      ...currentUser,
      storageUsage: Math.max(0, currentUser.storageUsage - incrementalTenantStorageUsage),
      tenantStorageUsage: Math.max(0, (currentUser.tenantStorageUsage ?? 0) + incrementalTenantStorageUsage),
    }
  })
}

export const setDoodleOwner = async (doodle: Doodle, ownerUserId: string) => {
  const tenantSharing = getFolder(doodle.parentFolderId)?.tenantSharing
  if (!(await withDefaultErrorHandler(patchJson(URL_API_DOODLE(doodle.id), { ownerUserId })))) {
    return
  }
  if (tenantSharing) {
    setDoodleIdDoodleMap(updateMap(map => map.set(doodle.id, { ...doodle, ownerUserId })))
  } else {
    setDoodleIdDoodleMap(updateMap(map => map.delete(doodle.id)))
    updateCurrentUser(currentUser => ({ ...currentUser, storageUsage: Math.max(0, currentUser.storageUsage - doodleStorageSize(doodle)) }))
  }
}

export const searchDoodles = async (where: QueryWhere) => {
  const whereJson = JSON.stringify(where)
  if (useSearchResultMap.snapshot().get(whereJson)?.doodleIds) {
    return
  }
  const request = fetch(`${URL_API_DOODLES}?where=${encodeURIComponent(whereJson)}`)
  const response = await withDefaultErrorHandler(request, notifyError)
  if (!response) {
    return
  }
  const result = (await response.json()) as { partialMatch: Doodle[]; partialMatchHitsTooMany?: boolean }
  const doodleIds = result.partialMatch.map(doodle => doodle.id)
  setDoodleIdDoodleMap(updateMap(map => result.partialMatch.forEach(doodle => map.set(doodle.id, doodle))))
  setSearchResultMap(updateMap(map => map.set(whereJson, { where, doodleIds, hitsTooMany: result.partialMatchHitsTooMany })))
}
